import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { NavBar } from '../components/NavBar';
import './Settings.css';

export function Settings() {
  const { user: authUser, getToken } = useAuth();
  const [activeTab, setActiveTab] = useState('password');

  // Password change
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  // User management
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showNewUser, setShowNewUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ name: '', email: '', password: '', role: 'SupplyManager' });
  const [newUserError, setNewUserError] = useState('');
  const [newUserSuccess, setNewUserSuccess] = useState('');
  const [newUserSaving, setNewUserSaving] = useState(false);

  // User editing
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  async function fetchUsers() {
    setUsersLoading(true);
    try {
      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setUsersLoading(false);
    }
  }

  async function handlePasswordChange(e) {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');
    if (!pwForm.currentPassword || !pwForm.newPassword || !pwForm.confirmPassword) {
      setPwError('All fields are required');
      return;
    }
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwError('New passwords do not match');
      return;
    }
    if (pwForm.newPassword.length < 8) {
      setPwError('New password must be at least 8 characters');
      return;
    }
    setPwSaving(true);
    try {
      const response = await fetch('/api/account/change-password', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to change password');
      }
      setPwSuccess('Password changed successfully');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPwError(err.message);
    } finally {
      setPwSaving(false);
    }
  }

  async function handleCreateUser(e) {
    e.preventDefault();
    setNewUserError('');
    setNewUserSuccess('');
    if (!newUserForm.name || !newUserForm.email || !newUserForm.password) {
      setNewUserError('All fields are required');
      return;
    }
    if (newUserForm.password.length < 8) {
      setNewUserError('Password must be at least 8 characters');
      return;
    }
    setNewUserSaving(true);
    try {
      const response = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newUserForm)
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create user');
      }
      setNewUserSuccess(`User ${newUserForm.name} created successfully`);
      setNewUserForm({ name: '', email: '', password: '', role: 'SupplyManager' });
      setShowNewUser(false);
      fetchUsers();
    } catch (err) {
      setNewUserError(err.message);
    } finally {
      setNewUserSaving(false);
    }
  }

  function startEdit(u) {
    setEditingUser(u.email);
    setEditForm({ name: u.name, role: u.role, isActive: u.isActive, newPassword: '' });
    setEditError('');
  }

  async function handleUpdateUser(email) {
    setEditSaving(true);
    setEditError('');
    try {
      const payload = {
        name: editForm.name,
        role: editForm.role,
        isActive: editForm.isActive
      };
      if (editForm.newPassword) payload.newPassword = editForm.newPassword;

      const response = await fetch(`/api/users/${encodeURIComponent(email)}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update user');
      }
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditSaving(false);
    }
  }

  return (
    <div className="page-container">
      <NavBar />
      <div className="settings-container">
        <div className="settings-header">
          <h2>Settings</h2>
        </div>

        <div className="settings-tabs">
          {['password', 'users'].map(tab => (
            <button
              key={tab}
              className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'password' ? 'Change Password' : 'User Management'}
            </button>
          ))}
        </div>

        {activeTab === 'password' && (
          <div className="settings-section">
            <h3>Change Password</h3>
            <p className="settings-desc">Logged in as <strong>{authUser?.email}</strong></p>
            <form onSubmit={handlePasswordChange} className="settings-form">
              <div className="form-field">
                <label>Current Password</label>
                <input type="password" value={pwForm.currentPassword} onChange={e => setPwForm({ ...pwForm, currentPassword: e.target.value })} placeholder="Enter current password" />
              </div>
              <div className="form-field">
                <label>New Password</label>
                <input type="password" value={pwForm.newPassword} onChange={e => setPwForm({ ...pwForm, newPassword: e.target.value })} placeholder="At least 8 characters" />
              </div>
              <div className="form-field">
                <label>Confirm New Password</label>
                <input type="password" value={pwForm.confirmPassword} onChange={e => setPwForm({ ...pwForm, confirmPassword: e.target.value })} placeholder="Repeat new password" />
              </div>
              {pwError && <div className="form-error">{pwError}</div>}
              {pwSuccess && <div className="form-success">{pwSuccess}</div>}
              <button type="submit" className="button-primary" disabled={pwSaving}>
                {pwSaving ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="settings-section">
            <div className="section-header-row">
              <h3>Users</h3>
              {authUser?.role === 'Admin' && (
                <button className="button-primary" onClick={() => setShowNewUser(!showNewUser)}>
                  {showNewUser ? 'Cancel' : '+ Add User'}
                </button>
              )}
            </div>

            {showNewUser && (
              <form onSubmit={handleCreateUser} className="settings-form new-user-form">
                <h4>New User</h4>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Full Name</label>
                    <input type="text" value={newUserForm.name} onChange={e => setNewUserForm({ ...newUserForm, name: e.target.value })} placeholder="Jane Smith" />
                  </div>
                  <div className="form-field">
                    <label>Email</label>
                    <input type="email" value={newUserForm.email} onChange={e => setNewUserForm({ ...newUserForm, email: e.target.value })} placeholder="jane@surveysherpa.com" />
                  </div>
                  <div className="form-field">
                    <label>Temporary Password</label>
                    <input type="password" value={newUserForm.password} onChange={e => setNewUserForm({ ...newUserForm, password: e.target.value })} placeholder="At least 8 characters" />
                  </div>
                  <div className="form-field">
                    <label>Role</label>
                    <select value={newUserForm.role} onChange={e => setNewUserForm({ ...newUserForm, role: e.target.value })}>
                      <option value="Admin">Admin</option>
                      <option value="SupplyManager">Supply Manager</option>
                    </select>
                  </div>
                </div>
                {newUserError && <div className="form-error">{newUserError}</div>}
                {newUserSuccess && <div className="form-success">{newUserSuccess}</div>}
                <button type="submit" className="button-primary" disabled={newUserSaving}>
                  {newUserSaving ? 'Creating...' : 'Create User'}
                </button>
              </form>
            )}

            {usersLoading ? (
              <div className="loading">Loading users...</div>
            ) : (
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Last Login</th>
                    <th>Status</th>
                    {authUser?.role === 'Admin' && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <React.Fragment key={u.email}>
                      <tr>
                        <td>{u.name}</td>
                        <td>{u.email}</td>
                        <td><span className={`role-badge role-${u.role?.toLowerCase()}`}>{u.role}</span></td>
                        <td>{u.lastLogin ? new Date(u.lastLogin).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Never'}</td>
                        <td>
                          <span className={`status-dot ${u.isActive ? 'active' : 'inactive'}`}>
                            {u.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        {authUser?.role === 'Admin' && (
                          <td>
                            <button className="edit-user-btn" onClick={() => editingUser === u.email ? setEditingUser(null) : startEdit(u)}>
                              {editingUser === u.email ? 'Cancel' : 'Edit'}
                            </button>
                          </td>
                        )}
                      </tr>
                      {editingUser === u.email && (
                        <tr className="edit-user-row">
                          <td colSpan={authUser?.role === 'Admin' ? 6 : 5}>
                            <div className="edit-user-form">
                              <div className="form-grid">
                                <div className="form-field">
                                  <label>Name</label>
                                  <input type="text" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
                                </div>
                                <div className="form-field">
                                  <label>Role</label>
                                  <select value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })}>
                                    <option value="Admin">Admin</option>
                                    <option value="SupplyManager">Supply Manager</option>
                                  </select>
                                </div>
                                <div className="form-field">
                                  <label>Status</label>
                                  <select value={editForm.isActive ? 'active' : 'inactive'} onChange={e => setEditForm({ ...editForm, isActive: e.target.value === 'active' })}>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                  </select>
                                </div>
                                <div className="form-field">
                                  <label>Reset Password <span className="optional">optional</span></label>
                                  <input type="password" value={editForm.newPassword || ''} onChange={e => setEditForm({ ...editForm, newPassword: e.target.value })} placeholder="Leave blank to keep current" />
                                </div>
                              </div>
                              {editError && <div className="form-error">{editError}</div>}
                              <button className="button-primary" onClick={() => handleUpdateUser(u.email)} disabled={editSaving}>
                                {editSaving ? 'Saving...' : 'Save Changes'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
